import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatIfDate } from './dateFormat';
import { authFetch, url } from './apiClient';
import FileViewer from './FileViewer';

// columns: [key, label] pairs, or [key, label, render(row)] for a custom cell.
function Section({ title, rows, columns }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 6 }}>{title} ({rows.length})</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead>
            <tr>{columns.map(([, label]) => <th key={label} style={th}>{label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 ? '#fff' : 'var(--surface)' }}>
                {columns.map(([key, , render]) => <td key={key} style={td}>{render ? render(row) : formatValue(row[key])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(formatIfDate(v));
}

function ArchiveDetail({ data }) {
  const [viewing, setViewing] = useState(null); // { path, title } of the file open in FileViewer
  // aadharStoragePath is "aadhaar/_archived/<user>/<stamp>/<file>"; null when the file was
  // already missing at move-out time.
  const documentCell = (o) => o.aadharStoragePath ? (
    <button
      type="button"
      onClick={() => setViewing({ path: `/uploads/${o.aadharStoragePath}`, title: `${o.name} — ${o.aadharFileName || 'Aadhaar'}` })}
      style={linkBtn}
    >
      View
    </button>
  ) : '—';
  return (
    <div style={detailBox}>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
        <strong>{data.fullName || 'Unnamed'}</strong>
        {data.mail && <> · {data.mail}</>}
        {data.phone && <> · {data.phone}</>}
        {data.moveInDate && <> · Moved in {formatDate(data.moveInDate)}</>}
        {data.demandedDeposit != null && <> · Demanded deposit ₹{data.demandedDeposit}</>}
      </div>
      <Section title="Bills" rows={data.bills} columns={[['monthYear', 'Month'], ['billType', 'Type'], ['rent', 'Rent'], ['water', 'Water'], ['electricity', 'Electricity'], ['miscellaneous', 'Misc'], ['paid', 'Paid'], ['paidDate', 'Paid Date'], ['paidVia', 'Paid Via']]} />
      <Section title="Complaints" rows={data.complaints} columns={[['description', 'Description'], ['status', 'Status'], ['createdDate', 'Created'], ['resolutionComment', 'Resolution']]} />
      <Section title="Occupants" rows={data.occupants} columns={[['name', 'Name'], ['aadharStoragePath', 'Document', documentCell], ['verified', 'Verified'], ['uploadedAt', 'Uploaded']]} />
      <Section title="Deposit Payments" rows={data.depositPayments} columns={[
        ['amount', 'Amount'],
        ['source', 'Paid', (d) => d.source === 'razorpay' ? 'Online' : ({ CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank transfer' }[d.method] || 'Manual')],
        ['reference', 'Payment ID / Ref', (d) => d.paymentId || d.reference || '—'],
        ['recordedBy', 'Recorded By'], ['notes', 'Notes'], ['paidDate', 'Paid / Received'],
      ]} />
      <Section title="Online Payment Log" rows={data.paymentLogs} columns={[
        ['timestamp', 'When'],
        // Rent and electricity are separate bills paid separately; older entries
        // (logged before the bill was recorded) show "—".
        ['billType', 'Bill', (p) => p.billType ? `${p.billType === 'ELECTRICITY' ? 'Electricity' : 'Rent'} · ${p.monthYear}` : '—'],
        ['amount', 'Amount'], ['paymentId', 'Razorpay Payment ID'], ['status', 'Status'],
      ]} />
      <Section title="Vacate History" rows={data.vacateRequests} columns={[
        ['requestedDate', 'Requested'], ['vacateDate', 'Move-out'], ['status', 'Status'],
        ['settlementDeduction', 'Deducted'], ['settlementRefundAmount', 'Refunded'], ['settlementRefundMethod', 'Method'],
        ['settlementNote', 'Owner Note'], ['tenantAcknowledged', 'Tenant Confirmed'], ['tenantFeedback', 'Tenant Feedback'],
      ]} />
      <Section title="Notes" rows={data.roomNotes} columns={[
        ['monthYear', 'Month'], ['positiveBalance', 'Positive Balance (Paid Extra)'],
        ['negativeBalance', 'Negative Balance (Not Paid)'], ['note', 'Note'], ['updatedAt', 'Saved'],
      ]} />
      {viewing && <FileViewer path={viewing.path} title={viewing.title} onClose={() => setViewing(null)} />}
    </div>
  );
}

export default function AdminArchivedTenants() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(url.adminArchivedTenants());
        if (!res.ok) throw new Error('Could not load archived tenants.');
        setRecords(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={box}>
      <h2 style={heading}>{t('nav.archivedTenants')}</h2>
      <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, marginTop: -12, marginBottom: 20 }}>
        Full history of tenants who have moved out and had their username freed up for reuse.
      </p>

      {error && <div style={errBox}>{error}</div>}
      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--muted)' }}>Loading…</p>
      ) : records.length === 0 ? (
        <p style={{ textAlign: 'center', color: 'var(--muted)' }}>No archived tenants yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {records.map((r) => (
            <div key={r.id} style={row}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{r.username}</strong>
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Archived {r.archivedDate ? formatDate(r.archivedDate) : '—'}
                    {r.archivedBy && <> by {r.archivedBy}</>}
                  </div>
                </div>
                <button type="button" onClick={() => setOpenId(openId === r.id ? null : r.id)} style={secondaryBtn}>
                  {openId === r.id ? 'Hide details' : 'View details'}
                </button>
              </div>
              {openId === r.id && <ArchiveDetail data={r.data} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const box = { maxWidth: 900, margin: '0 auto', padding: 28, background: 'var(--surface)', borderRadius: 16, boxShadow: 'var(--shadow)' };
const heading = { color: 'var(--primary)', textAlign: 'center', marginBottom: 8, letterSpacing: '1px', fontWeight: 'bold' };
const row = { background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 16px' };
const errBox = { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, fontWeight: 600, marginBottom: 12, textAlign: 'center' };
const secondaryBtn = { background: 'var(--line)', color: 'var(--ink-soft)', border: 'none', borderRadius: 999, padding: '8px 18px', fontWeight: 'bold', fontSize: 13, cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', padding: 0, color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 };
const detailBox = { marginTop: 10, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: 14 };
const table = { width: '100%', minWidth: 420, borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' };
const th = { padding: 8, background: 'var(--grad-primary)', color: '#fff', textAlign: 'center', fontSize: 12 };
const td = { padding: 6, textAlign: 'center', fontSize: 13, borderBottom: '1px solid var(--line)' };
